<template>
  <v-dialog :value="true" content-class="work-calendar" persistent fullscreen hide-overlay transition="dialog-bottom-transition">
    <div class="studio-cockpit-wrapper fill-height d-flex flex-column" style="background: #0f0f0f">
      <div class="d-flex justify-space-between align-center px-6 py-4 flex-shrink-0 z-index-20 header-bar">
        <div class="d-flex align-center">
          <v-btn icon large class="glass-btn mr-4" @click="$emit('back')">
            <v-icon color="white">mdi-arrow-left</v-icon>
          </v-btn>
          <div>
            <div class="text-h4 font-weight-black white--text text-shadow" style="line-height: 1.1">
              {{ $moment(currentDate).format("dddd") }}
            </div>
            <div class="d-flex align-center mt-1">
              <v-icon small color="blue lighten-2" class="mr-2">mdi-calendar-today</v-icon>
              <div class="text-subtitle-1 grey--text text--lighten-1 font-weight-bold">
                {{ $moment(currentDate).format("DD. MMMM YYYY") }}
              </div>
            </div>
          </div>
        </div>

        <div class="d-flex align-center glass-panel pa-2 rounded-xl">
          <v-btn icon @click="changeDate(-1)" class="mr-1"><v-icon color="grey lighten-1">mdi-chevron-left</v-icon></v-btn>
          <v-btn text class="white--text font-weight-bold px-4" @click="jumpToToday">Heute</v-btn>
          <v-btn icon @click="changeDate(1)" class="ml-1"><v-icon color="grey lighten-1">mdi-chevron-right</v-icon></v-btn>

          <v-menu offset-y :close-on-content-click="false" transition="scale-transition">
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon v-bind="attrs" v-on="on" class="ml-2"><v-icon color="blue lighten-2">mdi-calendar-search</v-icon></v-btn>
            </template>
            <v-date-picker v-model="currentDate" no-title scrollable dark color="blue accent-3" @input="fetchData"></v-date-picker>
          </v-menu>

          <v-btn icon class="ml-3" @click="sidebarOpen = !sidebarOpen" :color="sidebarOpen ? 'blue' : 'grey'">
            <v-icon>{{ sidebarOpen ? "mdi-page-layout-sidebar-right" : "mdi-page-layout-sidebar-right-off" }}</v-icon>
          </v-btn>
        </div>
      </div>

      <div class="flex-grow-1 d-flex overflow-hidden relative">
        <div class="flex-grow-1 d-flex flex-column relative border-right" style="min-width: 0">
          <div class="d-flex flex-grow-1 overflow-hidden">
            <div
              class="time-axis d-flex flex-column border-right custom-scrollbar"
              style="width: 70px; flex-shrink: 0; background: #0f0f0f; overflow-y: hidden"
              ref="timeAxis">
              <div style="height: 80px; flex-shrink: 0"></div>
              <div
                v-for="hour in hours"
                :key="hour"
                class="time-slot-label text-caption grey--text text--darken-1 text-right pr-3 relative"
                :style="{ height: hourHeight + 'px' }">
                <span style="position: absolute; top: -8px; right: 12px">{{ hour }}:00</span>
                <div style="position: absolute; top: 0; right: 0; width: 8px; height: 1px; background: #222"></div>
              </div>
            </div>

            <div class="d-flex flex-grow-1 overflow-auto custom-scrollbar relative" ref="scroller" @scroll="syncScroll">
              <div
                v-if="isToday && currentTimeTop > 0"
                class="current-time-line z-index-20"
                :style="{ top: currentTimeTop + 'px', width: viewColumns.length * 300 + 'px' }">
                <div class="time-badge">{{ $moment().format("HH:mm") }}</div>
              </div>

              <div
                v-if="!loading && viewColumns.length === 0"
                class="fill-height w-100 d-flex flex-column align-center justify-center grey--text text--darken-2">
                <v-icon size="80" color="grey darken-3" class="mb-4">mdi-store-off</v-icon>
                <div class="text-h6 grey--text text--darken-1">Heute keine Schichten geplant</div>
              </div>

              <work-calendar-column
                v-for="col in viewColumns"
                :key="col.id"
                :col="col"
                :hours-count="hours.length"
                :hour-height="hourHeight"
                :header-offset="headerOffset"
                :hover-state="hoverState"
                @drag-over="handleDragOver"
                @drop="handleDrop"
                @click-item="handleBlockClick"
                @click-empty="handleEmptyClick"
                @open-note="openNoteDialog" />
            </div>
          </div>
        </div>

        <transition name="slide-x-reverse-transition">
          <work-calendar-sidebar v-if="sidebarOpen" :templates="templates" @create="openCreateTemplate" @drag-start="handleDragStart" />
        </transition>
      </div>

      <work-calendar-drop-dialog v-model="dropDialog" :data="pendingDrop" @save="confirmDrop" />

      <work-calendar-edit-dialog
        v-model="editDialog"
        :edit-data="editData"
        :customer-details="customerDetails"
        :loading="customerLoading"
        @save="saveEdit"
        @delete="deleteItem"
        @action="triggerSpecialAction" />

      <v-dialog v-model="createTemplateDialog" max-width="600" persistent>
        <v-card class="glass-card-dark" dark>
          <v-card-title class="white--text dialog-title">Neue Vorlage erstellen</v-card-title>
          <v-card-text class="pt-4">
            <v-row dense>
              <v-col cols="12">
                <v-text-field v-model="newTemplate.title" label="Titel" outlined dense dark autofocus></v-text-field>
              </v-col>
              <v-col cols="6">
                <v-select v-model="newTemplate.type" :items="['appointment', 'task']" label="Typ" outlined dense dark>
                  <template v-slot:item="{ item }">{{ item === "appointment" ? "Termin (Zeit)" : "Ämtli (Aufgabe)" }}</template>
                </v-select>
              </v-col>
              <v-col cols="6" v-if="newTemplate.type === 'appointment'">
                <v-text-field v-model="newTemplate.duration" label="Dauer (Min)" type="number" outlined dense dark></v-text-field>
              </v-col>
              <v-col cols="12">
                <div class="caption grey--text mb-2">Farbe wählen</div>
                <div class="d-flex flex-wrap gap-2 mb-4">
                  <div
                    v-for="c in colorOptions"
                    :key="c"
                    class="color-dot cursor-pointer"
                    :class="{ active: newTemplate.color === c }"
                    :style="{ background: c }"
                    @click="newTemplate.color = c"></div>
                </div>
              </v-col>
              <v-col cols="12">
                <div class="caption grey--text mb-2">Icon wählen</div>
                <div class="icon-grid custom-scrollbar" style="max-height: 150px; overflow-y: auto">
                  <div
                    v-for="ic in iconOptions"
                    :key="ic.icon"
                    class="icon-item cursor-pointer text-center pa-2 rounded"
                    :class="{ 'blue darken-4': newTemplate.icon === ic.icon }"
                    @click="newTemplate.icon = ic.icon">
                    <v-icon :color="newTemplate.icon === ic.icon ? 'white' : 'grey'">{{ ic.icon }}</v-icon>
                    <div class="caption grey--text text-truncate mt-1" style="font-size: 10px">{{ ic.name }}</div>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" v-if="newTemplate.type === 'appointment'">
                <v-switch v-model="newTemplate.requiresCustomer" label="Erfordert Kunde?" dense inset dark></v-switch>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions class="px-4 pb-4">
            <v-spacer></v-spacer>
            <v-btn text color="grey lighten-1" @click="createTemplateDialog = false">Abbrechen</v-btn>
            <v-btn color="blue accent-3" depressed class="font-weight-bold black--text" @click="saveTemplate" :disabled="!newTemplate.title"
              >Erstellen</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="noteDialog" max-width="500">
        <v-card class="glass-card-dark" dark>
          <v-card-title class="white--text dialog-title">Notizen für {{ selectedCol?.info.firstName }}</v-card-title>
          <v-card-text class="pt-2">
            <div v-if="selectedCol?.notes.length > 0" class="mb-4">
              <div v-for="note in selectedCol.notes" :key="note._id" class="d-flex align-start pa-3 mb-2 glass-panel-light rounded elevation-1">
                <v-icon small color="blue lighten-2" class="mr-3 mt-1">mdi-message</v-icon>
                <div class="flex-grow-1">
                  <div class="white--text text-body-2" style="white-space: pre-wrap">{{ note.content }}</div>
                  <div class="text-caption grey--text mt-1">
                    Von {{ note.author ? note.author.vorname : "Team" }} • {{ $moment(note.createdAt).format("HH:mm") }}
                  </div>
                </div>
                <v-btn icon x-small color="red darken-2" @click="deleteNote(note._id)">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </div>
            </div>
            <div v-else class="text-center py-4 grey--text font-italic">Keine Notizen vorhanden.</div>
            <v-divider class="my-4 border-light"></v-divider>
            <v-textarea v-model="newNoteContent" outlined dense label="Nachricht schreiben..." rows="2" hide-details dark></v-textarea>
          </v-card-text>
          <v-card-actions class="px-4 pb-4">
            <v-spacer></v-spacer>
            <v-btn text color="grey" @click="noteDialog = false">Schließen</v-btn>
            <v-btn color="blue" depressed @click="createNote" :disabled="!newNoteContent">Senden</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </v-dialog>
</template>

<script>
import api from "@/api"
import userService from "@/services/userService"
import moment from "moment"

// Import Sub-Components
import WorkCalendarColumn from "./WorkCalendarColumn"
import WorkCalendarSidebar from "./WorkCalendarSidebar"
import WorkCalendarDropDialog from "./WorkCalendarDropDialog"
import WorkCalendarEditDialog from "./WorkCalendarEditDialog"

import { mapActions } from "vuex"
export default {
  name: "WorkCalendar",
  components: {
    WorkCalendarColumn,
    WorkCalendarSidebar,
    WorkCalendarDropDialog,
    WorkCalendarEditDialog,
  },
  data() {
    return {
      currentDate: moment().format("YYYY-MM-DD"),
      loading: false,
      customerLoading: false, // NEW: Loading state for customer details
      columns: [],
      viewColumns: [],
      templates: [],
      liveAttendanceMap: {},
      studioId: userService.getUser().studio._id,

      startHour: 6,
      endHour: 23,
      hourHeight: 100,
      headerOffset: 80,
      timerInterval: null,
      currentTimeTop: 0,
      sidebarOpen: true,
      hoverState: null,

      // Dialogs
      dropDialog: false,
      pendingDrop: null,
      editDialog: false,
      editData: {},
      customerDetails: null,
      createTemplateDialog: false,
      newTemplate: { title: "", type: "appointment", duration: 60, color: "#2196F3", icon: "mdi-calendar", requiresCustomer: false },
      noteDialog: false,
      selectedCol: null,
      newNoteContent: "",

      // Config Options
      iconOptions: [
        { name: "Training", icon: "mdi-dumbbell" },
        { name: "Plan", icon: "mdi-clipboard-list-outline" },
        { name: "Probetraining", icon: "mdi-account-star" },
        { name: "Check-Up", icon: "mdi-heart-pulse" },
        { name: "Ernährung", icon: "mdi-food-apple" },
        { name: "Kurs", icon: "mdi-yoga" },
        { name: "Verkauf", icon: "mdi-cash-register" },
        { name: "Meeting", icon: "mdi-account-group" },
        { name: "Büro", icon: "mdi-laptop" },
        { name: "Telefon", icon: "mdi-phone" },
        { name: "Reinigung", icon: "mdi-broom" },
        { name: "Wartung", icon: "mdi-tools" },
        { name: "Theke", icon: "mdi-coffee" },
        { name: "Rundgang", icon: "mdi-eye-check" },
        { name: "Müll", icon: "mdi-delete" },
        { name: "Inventar", icon: "mdi-package-variant" },
        { name: "Schliessen", icon: "mdi-door-closed-lock" },
        { name: "Öffnen", icon: "mdi-door-open" },
        { name: "Pause", icon: "mdi-pause-circle" },
        { name: "Event", icon: "mdi-party-popper" },
      ],
      colorOptions: ["#2196F3", "#4CAF50", "#FF9800", "#F44336", "#9C27B0", "#00BCD4", "#607D8B", "#E91E63"],
    }
  },
  computed: {
    hours() {
      const h = []
      for (let i = this.startHour; i <= this.endHour; i++) h.push(i)
      return h
    },
    isToday() {
      return moment(this.currentDate).isSame(moment(), "day")
    },
  },
  mounted() {
    this.fetchData()
    this.startClock()
  },
  beforeDestroy() {
    if (this.timerInterval) clearInterval(this.timerInterval)
  },
  methods: {
    ...mapActions("panelStore", ["selectCustomer", "clearCustomer", "setCustomerInfos"]),

    async fetchData() {
      this.loading = true
      try {
        const res = await api.shiftManagement.fetchDailyView(this.currentDate)
        this.columns = res.data.columns || []
        this.templates = res.data.templates || []
        await this.fetchLiveStatus()
        this.processViewData()
      } catch (e) {
        console.error(e)
      } finally {
        this.loading = false
        this.$nextTick(() => {
          if (this.isToday) this.scrollToNow()
        })
      }
    },

    processViewData() {
      this.viewColumns = this.columns
        .filter((col) => col.shifts && col.shifts.length > 0) // FILTER: Nur mit Schicht
        .map((col) => ({
          id: col.info._id,
          firstName: col.info.firstName,
          picture: col.info.picture,
          info: col.info,
          shifts: col.shifts,
          tasks: col.tasks,
          appointments: col.appointments,

          shiftRange: this.getShiftRange(col),
          liveStatus: this.getLiveStatus(col),
          notes: col.notes,
          backgrounds: this.calculateBackgrounds(col),
          items: this.calculateItems(col),
        }))
    },

    // --- ITEM ACTIONS ---
    async handleBlockClick(block, col) {
      if (block.type === "system") return

      this.editData = {
        id: block._id,
        isTask: block.isTask,
        title: block.title,
        description: block.rawData.description,
        color: block.borderColor,
        start: block.start,
        end: block.end,
        colId: col.id,
        specialType: block.rawData.specialType,
        customer: block.rawData.customer,
        duration: block.end ? moment(block.end, "HH:mm").diff(moment(block.start, "HH:mm"), "minutes") : 30,
      }

      this.customerDetails = null

      // 🚀 FETCH EXTENDED CUSTOMER DATA (ASYNC)
      if (this.editData.customer) {
        this.customerLoading = true
        this.editDialog = true // Open dialog immediately

        try {
          // Fetch full pool entry
          const poolData = await api.fetchCustomerInfosAndUpdatePool({ roodyUserId: this.editData.customer._id })
          this.customerDetails = poolData

          // STORE SELECTION: Set selected customer globally if store exists
          if (this.$store) {
            // Falls ihr ein "setCustomer" oder ähnliches habt
            this.slectCustomer(poolData)
          }
        } catch (e) {
          console.error("Customer info fetch failed", e)
        } finally {
          this.customerLoading = false
        }
      } else {
        this.editDialog = true
      }
    },

    // --- (Rest of the methods remain unchanged: isTimeSlotFree, handleDragOver, handleDrop, confirmDrop, saveEdit, deleteItem, triggerSpecialAction, etc.) ---
    isTimeSlotFree(col, startStr, endStr, excludeId = null) {
      const newStart = moment(startStr, "HH:mm")
      const newEnd = moment(endStr, "HH:mm")
      if (!col.backgrounds || !col.backgrounds.length) return "Mitarbeiter hat keine Schicht."
      let covered = false
      for (const bg of col.backgrounds) {
        const bgStart = moment(bg.start, "HH:mm")
        const bgEnd = moment(bg.end, "HH:mm")
        if (newStart.isSameOrAfter(bgStart) && newEnd.isSameOrBefore(bgEnd)) {
          covered = true
          break
        }
      }
      if (!covered) return "Außerhalb der Arbeitszeit."
      for (const item of col.items) {
        if (item._id === excludeId) continue
        const itemStart = moment(item.start, "HH:mm")
        let itemEndStr = item.end || moment(item.start, "HH:mm").add(30, "minutes").format("HH:mm")
        const itemEnd = moment(itemEndStr, "HH:mm")
        if (newStart.isBefore(itemEnd) && newEnd.isAfter(itemStart)) return `Kollision mit "${item.title}"`
      }
      return true
    },
    handleDragStart(tpl) {
      /* ... */
    },
    handleDragOver({ event, colId, relativeY }) {
      const hoursClicked = Math.max(0, relativeY / this.hourHeight)
      const totalMin = hoursClicked * 60
      const hour = this.startHour + Math.floor(totalMin / 60)
      const min = Math.round((totalMin % 60) / 15) * 15
      const timeStr = moment().hour(hour).minute(min).format("HH:mm")
      const topPx = (Math.max(0, (hour - this.startHour) * 60 + min) / 60) * this.hourHeight
      const col = this.viewColumns.find((c) => c.id === colId)
      const endTime = moment(timeStr, "HH:mm").add(60, "minutes").format("HH:mm")
      const validation = this.isTimeSlotFree(col, timeStr, endTime)
      const error = validation === true ? null : validation
      this.hoverState = { colId: colId, time: timeStr, top: topPx, height: this.hourHeight, error }
    },
    handleDrop({ event, col }) {
      this.hoverState = null
      const tplData = event.dataTransfer.getData("templateObj")
      if (!tplData) return
      const template = JSON.parse(tplData)
      const rect = event.currentTarget.querySelector(".lane-body").getBoundingClientRect()
      const relativeY = event.clientY - rect.top
      const totalMin = Math.max(0, relativeY / this.hourHeight) * 60
      const hour = this.startHour + Math.floor(totalMin / 60)
      const min = Math.round((totalMin % 60) / 15) * 15
      const timeStr = moment().hour(hour).minute(min).format("HH:mm")
      const duration = template.duration || 60
      const endM = moment(timeStr, "HH:mm").add(duration, "minutes")
      const valid = this.isTimeSlotFree(col, timeStr, endM.format("HH:mm"))
      if (valid !== true) {
        this.$toast.error(valid)
        return
      }
      this.pendingDrop = { template: { ...template }, col, time: timeStr, description: "" }
      this.dropDialog = true
    },
    async confirmDrop(data) {
      const { template, col, time, duration, description, customer } = data
      const endM = moment(`${this.currentDate} ${time}`, "YYYY-MM-DD HH:mm").add(duration, "minutes")
      const valid = this.isTimeSlotFree(col, time, endM.format("HH:mm"))
      if (valid !== true) {
        this.$toast.error(valid)
        return
      }
      try {
        if (template.type === "task") {
          await api.shiftManagement.createTask({
            date: this.currentDate,
            time,
            title: template.title,
            description,
            assignedEmployee: col.info._id,
            icon: template.icon,
            color: template.color,
          })
        } else {
          await api.shiftManagement.createAppointment({
            date: this.currentDate,
            start: time,
            end: endM.format("HH:mm"),
            title: template.title,
            description,
            assignedEmployee: col.info._id,
            customer: customer ? customer : null,
            icon: template.icon,
            color: template.color,
            specialType: template.specialType || null,
          })
        }
        this.$toast.success("Erstellt!")
        this.dropDialog = false
        this.fetchData()
      } catch (e) {
        this.$toast.error("Fehler beim Speichern")
      }
    },
    async saveEdit(data) {
      const col = this.viewColumns.find((c) => c.id === data.colId)
      let endStr = data.end
      if (!data.isTask) endStr = moment(data.start, "HH:mm").add(data.duration, "minutes").format("HH:mm")
      const valid = this.isTimeSlotFree(col, data.start, endStr, data.id)
      if (valid !== true) {
        this.$toast.error(valid)
        return
      }
      try {
        if (data.isTask)
          await api.shiftManagement.updateTask(data.id, { title: data.title, description: data.description, color: data.color, time: data.start })
        else
          await api.shiftManagement.updateAppointment(data.id, {
            title: data.title,
            description: data.description,
            color: data.color,
            start: data.start,
            end: endStr,
          })
        this.$toast.success("Gespeichert")
        this.editDialog = false
        this.fetchData()
      } catch (e) {
        this.$toast.error("Fehler")
      }
    },
    async deleteItem(data) {
      if (!confirm("Wirklich löschen?")) return
      try {
        if (data.isTask) await api.shiftManagement.deleteTask(data.id)
        else await api.shiftManagement.deleteAppointment(data.id)
        this.editDialog = false
        this.fetchData()
      } catch (e) {
        this.$toast.error("Fehler")
      }
    },
    triggerSpecialAction(data) {
      const type = data.specialType
      const cid = data.customer._id
      if (type === "workout") this.$router.push(`/workouts/createnew?userId=${cid}`)
      else if (type === "anamnese") this.$router.push(`/dashboard/user/search/${cid}`)
      else if (type === "upgrade") this.$router.push(`/dashboard/contract?userId=${cid}&upgrade=true`)
    },
    openCreateTemplate() {
      this.newTemplate = { title: "", type: "appointment", duration: 60, color: "#2196F3", icon: "mdi-calendar", requiresCustomer: false }
      this.createTemplateDialog = true
    },
    async saveTemplate() {
      try {
        await api.shiftManagement.createTemplate(this.newTemplate)
        this.$toast.success("Vorlage gespeichert")
        this.createTemplateDialog = false
        this.fetchData()
      } catch (e) {
        this.$toast.error("Fehler")
      }
    },
    openNoteDialog(col) {
      this.selectedCol = col
      this.newNoteContent = ""
      this.noteDialog = true
    },
    async createNote() {
      try {
        await api.shiftManagement.createNote({ date: this.currentDate, targetEmployee: this.selectedCol.info._id, content: this.newNoteContent })
        this.$toast.success("Gesendet")
        this.newNoteContent = ""
        this.fetchData()
      } catch (e) {
        this.$toast.error("Fehler")
      }
    },
    async deleteNote(id) {
      if (!confirm("Löschen?")) return
      try {
        await api.shiftManagement.deleteNote(id)
        this.fetchData()
      } catch (e) {}
    },
    handleEmptyClick(e, col) {
      this.$toast.info("Nutze die Vorlagen rechts!")
    },
    getTopFromTime(timeStr) {
      if (!timeStr) return 0
      const [h, m] = timeStr.split(":").map(Number)
      return (((h - this.startHour) * 60 + m) / 60) * this.hourHeight
    },
    getTop(hour, minute = 0) {
      return (((hour - this.startHour) * 60 + minute) / 60) * this.hourHeight
    },
    hexToRgba(hex, alpha) {
      if (!hex) return `rgba(100,100,100, ${alpha})`
      if (hex.startsWith("#")) {
        const r = parseInt(hex.slice(1, 3), 16)
        const g = parseInt(hex.slice(3, 5), 16)
        const b = parseInt(hex.slice(5, 7), 16)
        return `rgba(${r}, ${g}, ${b}, ${alpha})`
      }
      return hex
    },
    getShiftRange(col) {
      if (!col.shifts.length) return "-"
      let all = []
      col.shifts.forEach((s) => {
        if (s.times) all.push(...s.times)
      })
      if (!all.length) return "-"
      all.sort((a, b) => a.start.localeCompare(b.start))
      return `${all[0].start} - ${all[all.length - 1].end}`
    },
    calculateBackgrounds(col) {
      return col.shifts
        .map((s) => {
          if (!s.times || !s.times.length) return null
          const sorted = [...s.times].sort((a, b) => a.start.localeCompare(b.start))
          const top = this.getTopFromTime(sorted[0].start)
          const bottom = this.getTopFromTime(sorted[sorted.length - 1].end)
          const color = s.shiftTemplate?.color || "#333"
          return {
            top,
            height: bottom - top,
            color: color,
            bgColor: this.hexToRgba(color, 0.1),
            start: sorted[0].start,
            end: sorted[sorted.length - 1].end,
          }
        })
        .filter((x) => x)
    },
    calculateItems(col) {
      let items = []
      col.shifts.forEach((s) => {
        const times = s.times || []
        if (!times.length) return
        times.forEach((t) => {
          if (t.type === "Pause")
            items.push({
              title: "Pause",
              start: t.start,
              end: t.end,
              top: this.getTopFromTime(t.start),
              height: this.getTopFromTime(t.end) - this.getTopFromTime(t.start),
              class: "item-pause",
              borderColor: "#FF9800",
              icon: "mdi-coffee",
              iconColor: "orange",
              type: "system",
            })
        })
        if (s.shiftTemplate) {
          if (s.shiftTemplate.isOpeningShift) {
            const sorted = [...times].sort((a, b) => a.start.localeCompare(b.start))
            if (sorted.length > 0) {
              const startStr = sorted[0].start
              const endStr = moment(startStr, "HH:mm").add(30, "minutes").format("HH:mm")
              const top = this.getTopFromTime(startStr)
              items.push({
                title: "Centeröffnung",
                subtitle: "Fix",
                start: startStr,
                end: endStr,
                top,
                height: this.getTopFromTime(endStr) - top,
                class: "item-system glass-red",
                borderColor: "#D32F2F",
                icon: "mdi-door-open",
                iconColor: "red lighten-3",
                type: "system",
              })
            }
          }
          if (s.shiftTemplate.isClosingShift) {
            const sorted = [...times].sort((a, b) => a.start.localeCompare(b.start))
            if (sorted.length > 0) {
              const endStr = sorted[sorted.length - 1].end
              const startStr = moment(endStr, "HH:mm").subtract(30, "minutes").format("HH:mm")
              const top = this.getTopFromTime(startStr)
              items.push({
                title: "Centerschliessung",
                subtitle: "Fix",
                start: startStr,
                end: endStr,
                top,
                height: this.getTopFromTime(endStr) - top,
                class: "item-system glass-red",
                borderColor: "#D32F2F",
                icon: "mdi-door-closed",
                iconColor: "red lighten-3",
                type: "system",
              })
            }
          }
        }
      })
      col.tasks.forEach((t) => {
        items.push({
          _id: t._id,
          title: t.title,
          subtitle: t.description || "Aufgabe",
          start: t.time,
          end: "",
          top: this.getTopFromTime(t.time),
          height: (30 / 60) * this.hourHeight,
          class: "item-task glass-purple",
          borderColor: t.color || "#9C27B0",
          icon: t.icon || "mdi-checkbox-marked",
          iconColor: "white",
          isTask: true,
          rawData: t,
        })
      })
      col.appointments.forEach((a) => {
        const top = this.getTopFromTime(a.start)
        items.push({
          _id: a._id,
          title: a.title,
          subtitle: a.customer ? `${a.customer.vorname} ${a.customer.nachname}` : a.description,
          start: a.start,
          end: a.end,
          top,
          height: this.getTopFromTime(a.end) - top,
          class: "item-appt glass-blue",
          borderColor: a.color || "#2196F3",
          icon: a.icon || "mdi-calendar",
          iconColor: "white",
          isAppt: true,
          rawData: a,
        })
      })
      return items
    },
    getLiveStatus(col) {
      return this.liveAttendanceMap[col.info.employeeId]
        ? { active: true, color: "green accent-4", since: this.liveAttendanceMap[col.info.employeeId].checkin || "Jetzt" }
        : { active: false, color: "grey darken-3", since: "" }
    },
    changeDate(d) {
      this.currentDate = moment(this.currentDate).add(d, "days").format("YYYY-MM-DD")
      this.fetchData()
    },
    jumpToToday() {
      this.currentDate = moment().format("YYYY-MM-DD")
      this.fetchData()
    },
    syncScroll(e) {
      if (this.$refs.timeAxis) this.$refs.timeAxis.scrollTop = e.target.scrollTop
    },
    scrollToNow() {
      if (!this.$refs.scroller) return
      const nowMinutes = moment().hours() * 60 + moment().minutes()
      const offset = Math.max(0, nowMinutes - this.startHour * 60 - 60)
      this.$refs.scroller.scrollTop = (offset / 60) * this.hourHeight
    },
    startClock() {
      this.timerInterval = setInterval(() => {
        this.updateTimeLine()
        this.fetchLiveStatus()
      }, 30000)
    },
    updateTimeLine() {
      if (!this.isToday) {
        this.currentTimeTop = -100
        return
      }
      const now = moment()
      const h = now.hour()
      if (h < this.startHour || h > this.endHour) {
        this.currentTimeTop = -100
        return
      }
      this.currentTimeTop = this.getTop(h, now.minute())
    },
    async fetchLiveStatus() {
      try {
        let data = await api.shiftManagement.fetchAttendanceTrainers(this.studioId)
        const map = {}
        data.forEach((w) => {
          map[w._id] = w
        })
        this.liveAttendanceMap = map
      } catch (e) {}
    },
  },
}
</script>

<style scoped>
.header-bar {
  background: rgba(18, 18, 18, 0.95);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}
.glass-panel {
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
}
.glass-btn {
  background: rgba(255, 255, 255, 0.1);
}
.border-right {
  border-right: 1px solid rgba(255, 255, 255, 0.05);
}
.time-slot-label {
  border-bottom: 1px solid #222;
}
.current-time-line {
  position: absolute;
  left: 0;
  height: 2px;
  background: #ff5252;
  pointer-events: none;
  z-index: 20;
  box-shadow: 0 0 8px rgba(255, 82, 82, 0.6);
}
.time-badge {
  position: absolute;
  left: -50px;
  top: -10px;
  background: #ff5252;
  color: white;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: bold;
}
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #444;
  border-radius: 3px;
}
</style>
